import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import Fuse from 'fuse.js';
import { trimTextToEmpty } from 'flow/utils/message-utils';
import { makeBold } from 'flow/utils/element-utils';
var FIELDS_DISPLAY_FULL = ['email', 'fixedNumber', 'mobileNumber', 'altTelephone1', 'altTelephone2'];

/**
 * Use this to make a fuzzy search on the searchables argument.
 *
 * @param searchables is expected to be an immutable set
 * @param searchText is input string from a text input by a user
 * @param searchablesKeyMapper is required to map out what values the search should look for
 * If an entry with the name 'key' is supplied, it will not search for this value,
 * but good for connecting the search results to specific keys/items.
 * @param keys used for determining which fields should be searchable
 * @param options optional parameter for customizing fuzzy options
 *
 * Returns a JS object as supplied by the Fuse library and is affected by the options.
 */
export var fuzzySearch = function fuzzySearch(searchables, searchText, searchablesKeyMapper, keys, options) {
  if (!searchables) return [];
  return searchFuseInstance(createFuseInstance(searchables, searchablesKeyMapper, options, keys), searchText);
};
export var searchFuseInstance = function searchFuseInstance(fuseInstance, searchText) {
  if (!fuseInstance) return [];
  return fuzzySearchMatchAllWords(fuseInstance, trimTextToEmpty(searchText));
};
var DEFAULT_FUZZY_OPTIONS = {
  ignoreLocation: true,
  shouldSort: false,
  threshold: 0.3
};
var attachKeysToFuseOptions = function attachKeysToFuseOptions(options, keys) {
  return _objectSpread(_objectSpread({}, options), {}, {
    keys: keys
  });
};
export var createFuseInstance = function createFuseInstance(searchables, keyMapper, options, keys) {
  if (!searchables || searchables.isEmpty()) return null;
  if (!options) {
    options = DEFAULT_FUZZY_OPTIONS;
  }
  var fuseReadySearchables = searchables.map(keyMapper).valueSeq().toArray().filter(Boolean);
  var fuseReadyOptions = attachKeysToFuseOptions(options, keys);
  return new Fuse(fuseReadySearchables, fuseReadyOptions);
};
var fuzzySearchMatchAllWords = function fuzzySearchMatchAllWords(fuse, filterText) {
  if (fuse.options.useExtendedSearch === true) {
    var searchParts = filterText.split(' ').filter(function (part) {
      return part !== '';
    }).map(function (part) {
      return "'".concat(part);
    });
    return fuse.search({
      $and: searchParts.map(function (part) {
        return {
          $or: fuse.options.keys.map(function (k) {
            return _defineProperty({}, k.name, part);
          })
        };
      })
    });
  }
  var searchWordsArray = filterText.split(' ');
  var result = [];
  searchWordsArray.forEach(function (word, index) {
    var fuzzyResult = fuse.search(word);
    if (index === 0) {
      result = fuzzyResult;
    } else {
      var fuzzyResultKeys = fuzzyResult.map(function (r) {
        return r.item.key;
      });
      result = result.filter(function (o) {
        return fuzzyResultKeys.includes(o.item.key);
      });
      result.forEach(function (fuzzyObject) {
        // Extract matches and add to results for highlighting
        var itemWithNewData = fuzzyResult.find(function (f) {
          return f.item.key === fuzzyObject.item.key;
        });
        if (fuse.options.includeMatches) {
          var _fuzzyObject$matches;
          (_fuzzyObject$matches = fuzzyObject.matches).push.apply(_fuzzyObject$matches, _toConsumableArray(itemWithNewData.matches));
        }
        fuzzyObject.score = fuzzyObject.score + itemWithNewData.score;
      });
    }
  });

  // If we have more than one word we try to find a match where one field includes the
  // entire search string. If we do, we replace the prevoius match with that and
  // prioritize it by subtracting 1 from its score.
  if (searchWordsArray.length > 1) {
    var includesMatch = fuse.search("'\"".concat(filterText, "\""));
    var includesMatchKeys = includesMatch.map(function (o) {
      return o.item.key;
    });
    result.filter(function (o) {
      return includesMatchKeys.includes(o.item.key);
    }).forEach(function (fuzzyObject) {
      var newItem = includesMatch.find(function (f) {
        return f.item.key === fuzzyObject.item.key;
      });
      if (fuse.options.includeMatches) {
        var _fuzzyObject$matches2;
        (_fuzzyObject$matches2 = fuzzyObject.matches).push.apply(_fuzzyObject$matches2, _toConsumableArray(newItem.matches));
      }
      fuzzyObject.score = newItem.score - 1;
    });
  }
  return result;
};

/**
 * Will return an array with the match results that are ready to be rendered by the dom.
 * [nameResult, otherResult]
 * Will never return values on both variables.
 * If there is a match on the name, we don't want to display other matches.
 */
export var getParsedFuzzyTexts = function getParsedFuzzyTexts(matches, i18next, translationRoot) {
  if (!matches) return [];
  var nameResult;
  var otherResult;
  var nameMatches = matches.filter(function (match) {
    return match['key'] === 'name';
  });
  nameMatches.forEach(function (match) {
    var _nameResult$match$key;
    if (!nameResult) {
      nameResult = {};
      nameResult[match.key] = {
        value: match.value,
        indices: []
      };
    }
    (_nameResult$match$key = nameResult[match.key].indices).push.apply(_nameResult$match$key, _toConsumableArray(match.indices));
  });
  if (!nameResult) {
    var otherMatches = matches.filter(function (match) {
      return match['key'] !== 'name';
    });
    otherMatches.forEach(function (match) {
      var _otherResult$match$ke;
      if (!otherResult) otherResult = {};
      if (!otherResult[match.key]) otherResult[match.key] = {
        value: match.value,
        indices: []
      };
      (_otherResult$match$ke = otherResult[match.key].indices).push.apply(_otherResult$match$ke, _toConsumableArray(match.indices));
    });
  }
  return [getFuzzyParsedName(nameResult), getFuzzyParsedOther(otherResult, i18next, translationRoot)];
};

/**
 * Returns an array that is ready to be rendered by the dom
 * if there are any matches on the name
 */
export var getFuzzyParsedName = function getFuzzyParsedName(nameResults) {
  if (!nameResults || !nameResults.name) return null;
  if (nameResults.name.indices.length === 0) return nameResults.name;
  var name = nameResults.name.value;
  return makeBold(name, nameResults.name.indices);
};

/**
 * Returns an array that is ready to be rendered by the dom
 */
export var getFuzzyParsedOther = function getFuzzyParsedOther(otherResults, i18next, translationRoot) {
  if (!otherResults) return null;
  var matchesBolded = Object.keys(otherResults).map(function (key) {
    var value = otherResults[key].value;
    var bolded = makeBold(value, otherResults[key].indices);
    if (!FIELDS_DISPLAY_FULL.includes(key) && value.length > 15) {
      var firstIndexCut = Math.max(bolded[0].length - 4, 0);
      bolded[0] = bolded[0].slice(firstIndexCut);
      bolded[bolded.length - 1] = bolded[bolded.length - 1].slice(0, 4);
    }
    return {
      key: key,
      text: bolded
    };
  });
  return Object.values(matchesBolded).reduce(function (acc, match, index) {
    if (index !== 0) {
      acc.push(', ');
    }
    var translationKey = "".concat(translationRoot, ".").concat(match.key);
    acc.push(["".concat(i18next.t(translationKey, match.key), ": ")].concat(_toConsumableArray(match.text)));
    return acc;
  }, []);
};